import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    IconButton
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { TourFormData } from '../ScheduleTourFlow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import isoWeek from 'dayjs/plugin/isoWeek';
import { config } from '../../../config/env';

dayjs.extend(isoWeek);

interface TimePickerStepProps {
    school: string;
    selectedDate: string;
    selectedTime: string;
    onNext: () => void;
    onBack: () => void;
    updateFormData: (data: Partial<TourFormData>) => void;
    rescheduleToken?: string; // Optional token for rescheduling
    isSubmitting?: boolean; // Optional flag to disable Next button during submission
}

interface WeekDay {
    date: Dayjs;
    dayName: string;
    dateNum: string;
    times: string[];
}

interface DailyTourTimes {
    day_of_week: number;
    date: string;
    available_times: string[];
}

interface WeeklyAvailableTourTimes {
    days: DailyTourTimes[];
}

function TimePickerStep({ school, selectedDate, selectedTime, onNext, onBack, updateFormData, rescheduleToken, isSubmitting = false }: TimePickerStepProps) {
    // Calculate min and max dates (today through 2 weeks from now)
    const minDate = dayjs().startOf('day');
    const maxDate = dayjs().add(2, 'week').endOf('day');

    // For mobile, calculate which day index today is (0-4, Mon-Fri)
    const getTodayDayIndex = () => {
        const today = dayjs();
        const dayOfWeek = today.day(); // 0=Sun, 1=Mon, ..., 6=Sat
        // Convert to 0-4 (Mon-Fri), default to 0 if weekend
        if (dayOfWeek === 0 || dayOfWeek === 6) return 0; // Weekend -> Monday
        return dayOfWeek - 1; // Mon=0, Tue=1, ..., Fri=4
    };

    const [currentWeekStart, setCurrentWeekStart] = useState<Dayjs>(
        selectedDate ? dayjs(selectedDate).startOf('isoWeek') : dayjs().startOf('isoWeek')
    );
    const [currentDayIndex, setCurrentDayIndex] = useState<number>(getTodayDayIndex()); // For mobile: default to today
    const [selectedDateTime, setSelectedDateTime] = useState<{ date: string; time: string } | null>(
        selectedDate && selectedTime ? { date: selectedDate, time: selectedTime } : null
    );
    const [weekData, setWeekData] = useState<WeekDay[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Calculate if we can navigate to previous/next week
    const canGoPrevious = currentWeekStart.isAfter(minDate);
    const canGoNext = currentWeekStart.add(1, 'week').isBefore(maxDate);

    // Calculate if we can navigate to previous/next day (mobile)
    // Can't go before today
    const currentDayDate = currentWeekStart.add(currentDayIndex, 'day');
    const canGoPreviousDay = currentDayDate.isAfter(minDate, 'day');
    const canGoNextDay = currentDayDate.isBefore(maxDate, 'day');

    const fetchWeekAvailability = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            // Use the Monday of the current week as the starting date
            const startDate = currentWeekStart.format('YYYY-MM-DD');

            const url = new URL('/v1/available-tour-times', config.apiBaseUrl);
            url.searchParams.append('school', school);
            url.searchParams.append('date', startDate);
            if (rescheduleToken) {
                url.searchParams.append('token', rescheduleToken);
            }

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'Referer': window.location.href,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to fetch tour times');
            }

            const apiResponse = await response.json();
            const data: WeeklyAvailableTourTimes = apiResponse.message;

            // Transform API response to match our UI structure (only Mon-Fri)
            const weekDays: WeekDay[] = data.days
                .filter(day => day.day_of_week < 5) // Only weekdays (0-4)
                .map(day => ({
                    date: dayjs(day.date),
                    dayName: dayjs(day.date).format('ddd'),
                    dateNum: dayjs(day.date).format('MMM D'),
                    times: day.available_times,
                }));

            setWeekData(weekDays);
        } catch (err) {
            setError('Failed to load available times. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [currentWeekStart, school, rescheduleToken]);

    // Fetch available times for the current week
    useEffect(() => {
        if (school) {
            void fetchWeekAvailability();
        }
    }, [school, fetchWeekAvailability]);

    const handlePreviousWeek = () => {
        if (canGoPrevious) {
            setCurrentWeekStart(currentWeekStart.subtract(1, 'week'));
            setCurrentDayIndex(0); // Reset to Monday
        }
    };

    const handleNextWeek = () => {
        if (canGoNext) {
            setCurrentWeekStart(currentWeekStart.add(1, 'week'));
            setCurrentDayIndex(0); // Reset to Monday
        }
    };

    const handlePreviousDay = () => {
        const nextDayDate = currentWeekStart.add(currentDayIndex - 1, 'day');

        // Don't allow going before today
        if (!nextDayDate.isBefore(minDate, 'day')) {
            if (currentDayIndex > 0) {
                setCurrentDayIndex(currentDayIndex - 1);
            } else if (canGoPrevious) {
                // Go to previous week, Friday
                setCurrentWeekStart(currentWeekStart.subtract(1, 'week'));
                setCurrentDayIndex(4);
            }
        }
    };

    const handleNextDay = () => {
        const nextDayDate = currentWeekStart.add(currentDayIndex + 1, 'day');

        // Don't allow going beyond max date
        if (!nextDayDate.isAfter(maxDate, 'day')) {
            if (currentDayIndex < 4) {
                setCurrentDayIndex(currentDayIndex + 1);
            } else if (canGoNext) {
                // Go to next week, Monday
                setCurrentDayIndex(0);
                setCurrentWeekStart(currentWeekStart.add(1, 'week'));
            }
        }
    };

    const handleTimeSelect = (date: Dayjs, time: string) => {
        const dateTime = {
            date: date.format('YYYY-MM-DD'),
            time,
        };
        setSelectedDateTime(dateTime);

        // Update form data and proceed to next step
        updateFormData({
            tourDate: dateTime.date,
            tourTime: dateTime.time,
        });

        // Scroll to top before navigating
        window.scrollTo({ top: 0, behavior: 'smooth' });
        onNext();
    };

    const handleNext = () => {
        if (selectedDateTime) {
            updateFormData({
                tourDate: selectedDateTime.date,
                tourTime: selectedDateTime.time,
            });

            // Scroll to top before navigating
            window.scrollTo({ top: 0, behavior: 'smooth' });
            onNext();
        }
    };

    const handleBack = () => {
        // Scroll to top before navigating
        window.scrollTo({ top: 0, behavior: 'smooth' });
        onBack();
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Pick a date and time for your tour
                </Typography>

                <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    Available tours from today through the next 2 weeks
                </Typography>

                {error && <Alert severity="error">{error}</Alert>}

                {/* Desktop Week Navigation */}
                <Box sx={{
                    display: 'none',
                    '@media (min-width: 750px)': {
                        display: 'flex',
                    },
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    mb: 2
                }}>
                    <IconButton
                        onClick={handlePreviousWeek}
                        disabled={!canGoPrevious}
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1, textAlign: 'center' }}>
                        Week of {currentWeekStart.format('MMM D')}
                    </Typography>
                    <IconButton
                        onClick={handleNextWeek}
                        disabled={!canGoNext}
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Box>

                {/* Mobile Day Navigation */}
                {!loading && (
                    <Box sx={{
                        display: 'flex',
                        '@media (min-width: 750px)': {
                            display: 'none',
                        },
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        mb: 2
                    }}>
                        <IconButton
                            onClick={handlePreviousDay}
                            disabled={!canGoPreviousDay}
                            sx={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1, textAlign: 'center' }}>
                            {weekData[currentDayIndex] ? `${weekData[currentDayIndex].dayName}, ${weekData[currentDayIndex].dateNum}` : ''}
                        </Typography>
                        <IconButton
                            onClick={handleNextDay}
                            disabled={!canGoNextDay}
                            sx={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>
                )}

                {/* Weekly View Grid */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Desktop: Show all 5 days */}
                        <Box sx={{
                            display: 'none',
                            '@media (min-width: 750px)': {
                                display: 'grid',
                            },
                            gridTemplateColumns: 'repeat(5, 1fr)',
                            gap: 2,
                            width: '100%'
                        }}>
                            {weekData.map((day) => (
                                <Paper
                                    key={day.date.format('YYYY-MM-DD')}
                                    elevation={3}
                                    sx={{
                                        p: 2,
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        minHeight: '300px',
                                        maxHeight: '500px',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {/* Day Header */}
                                    <Box sx={{ textAlign: 'center', mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            {day.dayName}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {day.dateNum}
                                        </Typography>
                                    </Box>

                                    {/* Time Slots with custom scrollbar */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        overflowY: 'auto',
                                        mx: -2, // Negative margin to extend to edges
                                        px: 2,  // Add padding inside for content
                                        '&::-webkit-scrollbar': {
                                            width: '3px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: 'transparent',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: 'rgba(0, 0, 0, 0.3)',
                                            borderRadius: '3px',
                                        },
                                        '&::-webkit-scrollbar-thumb:hover': {
                                            background: 'rgba(0, 0, 0, 0.5)',
                                        },
                                        // Firefox scrollbar styling
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: 'rgba(0, 0, 0, 0.3) transparent',
                                    }}>
                                        {day.times.length === 0 ? (
                                            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary', py: 2 }}>
                                                No tours available
                                            </Typography>
                                        ) : (
                                            day.times.map((time) => {
                                                const isSelected =
                                                    selectedDateTime?.date === day.date.format('YYYY-MM-DD') &&
                                                    selectedDateTime?.time === time;

                                                return (
                                                    <Button
                                                        key={time}
                                                        variant={isSelected ? 'contained' : 'outlined'}
                                                        size="small"
                                                        onClick={() => handleTimeSelect(day.date, time)}
                                                        sx={{
                                                            fontSize: '0.75rem',
                                                            py: 1,
                                                            backgroundColor: isSelected
                                                                ? 'primary.main'
                                                                : 'rgba(255, 255, 255, 0.5)',
                                                            '&:hover': {
                                                                backgroundColor: isSelected
                                                                    ? 'primary.dark'
                                                                    : 'rgba(255, 255, 255, 0.8)',
                                                            },
                                                        }}
                                                    >
                                                        {time}
                                                    </Button>
                                                );
                                            })
                                        )}
                                    </Box>
                                </Paper>
                            ))}
                        </Box>

                        {/* Mobile: Show only current day */}
                        <Box sx={{
                            display: 'block',
                            '@media (min-width: 750px)': {
                                display: 'none',
                            },
                            width: '100%'
                        }}>
                            {weekData[currentDayIndex] && (
                                <Paper
                                    elevation={3}
                                    sx={{
                                        p: 3,
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        minHeight: '300px',
                                        maxHeight: '500px',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    {/* Time Slots with custom scrollbar */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        overflowY: 'auto',
                                        mx: -3, // Negative margin to extend to edges
                                        px: 3,  // Add padding inside for content
                                        '&::-webkit-scrollbar': {
                                            width: '3px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: 'transparent',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: 'rgba(0, 0, 0, 0.3)',
                                            borderRadius: '3px',
                                        },
                                        '&::-webkit-scrollbar-thumb:hover': {
                                            background: 'rgba(0, 0, 0, 0.5)',
                                        },
                                        // Firefox scrollbar styling
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: 'rgba(0, 0, 0, 0.3) transparent',
                                    }}>
                                        {weekData[currentDayIndex].times.length === 0 ? (
                                            <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                                                No tours available for this day
                                            </Typography>
                                        ) : (
                                            weekData[currentDayIndex].times.map((time) => {
                                                const isSelected =
                                                    selectedDateTime?.date === weekData[currentDayIndex].date.format('YYYY-MM-DD') &&
                                                    selectedDateTime?.time === time;

                                                return (
                                                    <Button
                                                        key={time}
                                                        variant={isSelected ? 'contained' : 'outlined'}
                                                        size="large"
                                                        onClick={() => handleTimeSelect(weekData[currentDayIndex].date, time)}
                                                        sx={{
                                                            fontSize: '1rem',
                                                            py: 1.5,
                                                            backgroundColor: isSelected
                                                                ? 'primary.main'
                                                                : 'rgba(255, 255, 255, 0.5)',
                                                            '&:hover': {
                                                                backgroundColor: isSelected
                                                                    ? 'primary.dark'
                                                                    : 'rgba(255, 255, 255, 0.8)',
                                                            },
                                                        }}
                                                    >
                                                        {time}
                                                    </Button>
                                                );
                                            })
                                        )}
                                    </Box>
                                </Paper>
                            )}
                        </Box>
                    </>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        startIcon={<ArrowBackIcon />}
                        size="large"
                    >
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        disabled={!selectedDateTime || isSubmitting}
                        size="large"
                    >
                        {isSubmitting ? <CircularProgress size={24} /> : 'Next'}
                    </Button>
                </Box>
            </Box>
        </LocalizationProvider>
    );
}

export default TimePickerStep;
